//this file is ProcessForm.jsx

import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { TextField, FormControl } from '@mui/material';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import TextInput from 'src/components/UI/input/TextInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import ConversionSuggestionCard from './ConversionSuggestionCard';
import {
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext, AppContext, UserContext } from 'src/store/ContextStore';

export default function ProcessForm({
  data,
  handleClose,
  familyMemberId,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  isDisabled,
  setUnsavedChanges
}) {
  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { user: loggedInUser } = React.useContext(UserContext);
  const { setSnack } = React.useContext(SnackContext);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const defaultValues = isEditing
    ? {
        ...model,
        staffMember: Array.isArray(model.staffMember) ? model.staffMember : []
      }
    : {
        familyId: user.familyId,
        familyMemberId,
        serviceProvider: '',
        processType: '',
        processStatus: '',
        processStartDate: null,
        endDate: null,
        processComment: '',
        familyNote: false,
        displayInPedigree: false,
        private: false,
        staffMember: loggedInUser?._id ? [loggedInUser._id] : []
      };

  const [formValues, setFormValues] = React.useState(defaultValues);

  // Add-as-you-type option lists, persisted as clinical defaults (same
  // mechanism + permissions as the Appointment "type" field). New values are
  // creatable by admin / genetics / geneticsServiceAdmin via AutocompleteInput.
  const [serviceProviderOptions, setServiceProviderOptions] = React.useState(
    []
  );
  const [processTypeOptions, setProcessTypeOptions] = React.useState([]);
  const [processStatusOptions, setProcessStatusOptions] = React.useState([]);

  React.useEffect(() => {
    getClinicalDefaults('serviceProviders').then(setServiceProviderOptions);
    getClinicalDefaults('processTypes').then(setProcessTypeOptions);
    getClinicalDefaults('processStatuses').then(setProcessStatusOptions);
  }, []);

  const handleChange = (e) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const onToggleSwitch = (e) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const allStaffMembers = React.useMemo(
    () => staffMemberOptions.map((o) => o.value),
    [staffMemberOptions]
  );
  const staffMembersMap = React.useMemo(
    () =>
      staffMemberOptions.reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    [staffMemberOptions]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Resolve selected staff userIds back to the stored {userId, firstName,
    // lastName} shape (same as the other clinical-note forms).
    let staffMember = [];
    if (
      Array.isArray(formValues.staffMember) &&
      formValues.staffMember.length
    ) {
      staffMember = formValues.staffMember
        .map((id) => staffMembers.find((m) => m.userId === id))
        .filter(Boolean)
        .map((m) => ({ ...m }));
    }

    const payload = {
      ...formValues,
      staffMember
    };

    return axios
      .put(APICONSTANTS.process, payload)
      .then(() => {
        handleClose({ exit: true });
        setSnack({
          status: true,
          msg: 'Successfully saved',
          severity: 'success'
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Save failed! ${err}`,
          severity: 'error'
        });
      });
  };

  const deleteNote = () => {
    setLoading(true);
    axios
      .delete(`/clinical-notes/note`, { params: { _id: model._id } })
      .then(() => {
        setSnack({ status: true, msg: 'Record deleted', severity: 'success' });
        setOpen(false);
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={() => setOpen(false)}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Process ?"
        content="Warning: This action will permanently delete this Process. Once deleted, this information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Service Provider</FormLabel>
          </Grid>
          <AutocompleteInput
            name="serviceProvider"
            value={formValues.serviceProvider || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, serviceProvider: label }));
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
              if (label) {
                saveClinicalDefault('serviceProviders', label).then(
                  setServiceProviderOptions
                );
              }
            }}
            options={serviceProviderOptions}
            onDelete={(option) => {
              deleteClinicalDefault('serviceProviders', option.label).then(
                setServiceProviderOptions
              );
            }}
            helperText="Please select or enter a service provider"
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Type</FormLabel>
          </Grid>
          <AutocompleteInput
            name="processType"
            value={formValues.processType || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, processType: label }));
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
              if (label) {
                saveClinicalDefault('processTypes', label).then(
                  setProcessTypeOptions
                );
              }
            }}
            options={processTypeOptions}
            onDelete={(option) => {
              deleteClinicalDefault('processTypes', option.label).then(
                setProcessTypeOptions
              );
            }}
            helperText="Please select or enter a process type"
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Status</FormLabel>
          </Grid>
          <AutocompleteInput
            name="processStatus"
            value={formValues.processStatus || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              setFormValues((prev) => ({ ...prev, processStatus: label }));
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
              if (label) {
                saveClinicalDefault('processStatuses', label).then(
                  setProcessStatusOptions
                );
              }
            }}
            options={processStatusOptions}
            onDelete={(option) => {
              deleteClinicalDefault('processStatuses', option.label).then(
                setProcessStatusOptions
              );
            }}
            helperText="Please select or enter a process status"
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            type="date"
            label="Process Start Date"
            name="processStartDate"
            value={
              formValues.processStartDate
                ? new Date(formValues.processStartDate)
                : null
            }
            onChange={(e) => {
              setFormValues((prev) => ({ ...prev, processStartDate: e }));
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
            }}
            minDate={new Date(1600, 0, 0)}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            type="date"
            label="End Date"
            name="endDate"
            value={formValues.endDate ? new Date(formValues.endDate) : null}
            onChange={(e) => {
              setFormValues((prev) => ({ ...prev, endDate: e }));
              setUnsavedChanges((prev) => ({ ...prev, edit: true }));
            }}
            minDate={new Date(1600, 0, 0)}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <TextInput
              label="Staff Member"
              name="staffMember"
              type="multiple-select"
              value={formValues.staffMember || []}
              onChange={handleChange}
              options={allStaffMembers || []}
              mapper={staffMembersMap || {}}
              disabled={isDisabled}
            />
          </FormControl>
        </Grid>

        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Process Comment</FormLabel>
          </Grid>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            name="processComment"
            value={formValues.processComment || ''}
            onChange={handleChange}
            slotProps={{
              htmlInput: { readOnly: isDisabled }
            }}
          />
        </Grid>

        {/* The three flags.
            `private` is READ-ONLY in Cloud (TGCLOUD-1753). The flag originates
            in Desktop, and Cloud now honours it by excluding flagged rows from
            every read. Leaving this switch operable would therefore be a
            one-way door: ticking it would make the record vanish from the very
            screen you ticked it on, with no way to find it again and clear it.
            The row stays rendered so the flag is not invisible in the data
            model, but in practice it will always read "No" here: a record that
            IS private is excluded from every read, so this form never opens for
            one. Clearing an already-private record is therefore not possible
            from Cloud at all — see TGCLOUD-1766, which scopes what the flag
            means in Desktop before Cloud decides whether to manage it. */}
        {[
          { name: 'familyNote', label: 'Family Note' },
          { name: 'displayInPedigree', label: 'Display in Pedigree' },
          { name: 'private', label: 'Private', readOnly: true }
        ].map(({ name, label, readOnly }) => (
          <Grid
            container
            key={name}
            size={12}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 6,
                sm: 7
              }}
            >
              <FormLabel component="label" className="form-label">
                {label}
              </FormLabel>
            </Grid>
            <Grid
              container
              size={{
                xs: 6,
                sm: 3
              }}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid>
                <Switch
                  color="secondary"
                  name={name}
                  checked={formValues[name] ?? false}
                  onChange={onToggleSwitch}
                  disabled={isDisabled || readOnly}
                />
              </Grid>
              <Grid>
                <Typography>{formValues[name] ? 'Yes' : 'No'}</Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}

        {/* Opt-in conversion suggestion — appears on open/edit, never converts
            on save. Shown once a Process Type is present. */}
        {formValues.processType?.trim() && (
          <Grid size={12}>
            <ConversionSuggestionCard
              process={formValues}
              probandUserId={data?._id}
              data={data}
              user={user}
            />
          </Grid>
        )}

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={
            isEditing && !isDisabled ? () => setOpen(true) : null
          }
          addLabel={`Add new process`}
        />
      </Grid>
    </form>
  );
}
